import { Outlet } from 'react-router';
import { RoutePaths } from '@/routing/RoutePaths';
import { Card, CardHeader, CardBody, Spinner, Avatar, Button, Link } from '@heroui/react';
import { Loading } from '@livefree/react-ui';
import { useSuccessReactor } from '@livefree/reactive';
import { useOAuth2 } from '@livefree/react-oauth2';
import { Delay } from '@/shared/components';
import { withAuthorization } from '@livefree/applications';
import { reactive } from '@/core/reactive/reactive';
import { hostRuntime } from '@/core/runtime';

export const Layout = reactive.component(
  withAuthorization(
    ({ services }) => {
      const {
        user,
        isAuthenticated,
        isAuthenticating,
        authenticationRequired,
        signingOut,
        signIn,
        signOut,
      } = useOAuth2();

      if (authenticationRequired) {
        signIn();
      }

      useSuccessReactor(services.my.clearCache, () => signOut());

      return (
        <Card className="w-full max-w-sm bg-white/0 p-0">
          <CardHeader className="relative flex h-[100px] flex-col justify-end overflow-visible bg-white/30 backdrop-blur-md p-4">
            {isAuthenticated && (
              <Avatar className="h-20 w-20 translate-y-12 shrink-0" src={user.picture} />
            )}
            {isAuthenticated && (
              <Button
                className="absolute right-3 top-3 bg-white/20 text-white dark:bg-black/20"
                radius="full"
                size="sm"
                variant="light"
                isLoading={signingOut}
                isDisabled={signingOut}
                onPress={() => services.my.clearCache()}
              >
                Logout
              </Button>
            )}
          </CardHeader>
          <CardBody className="bg-white p-4 pt-10">
            {isAuthenticating && (
              <>
                <Spinner label="Loading..." />
                <Delay seconds={10}>
                  <div className="pt-2 text-xs w-full text-center">
                    This is taking longer than expected, please{' '}
                    <Link
                      onPress={() => (window.location.pathname = '/')}
                      className="text-xs cursor-pointer"
                    >
                      reload the window.
                    </Link>
                  </div>
                </Delay>
              </>
            )}
            {isAuthenticated && <p className="text-large font-medium px-1">{user.displayName}</p>}
            {isAuthenticated && <Outlet />}
          </CardBody>
        </Card>
      );
    },
    {
      applicationKey: hostRuntime.config.auth.applicationKey,
      authorityUrl: hostRuntime.config.auth.host,
      clientId: hostRuntime.config.auth.clientId,
      authorizationParams: {
        scope: hostRuntime.config.auth.scopes,
      },
      handlers: {
        onCodeExchangeError: () => (window.location = RoutePaths().root()),
        onSignedOutRemotely: () => (window.location = RoutePaths().root()),
      },
    },
    () => <Loading />,
  ),
  {
    name: 'ProfileLayout',
  },
);
